// Depolar — paylaşılan UI bileşenleriyle yeniden tasarlandı.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import DataTable from '../components/DataTable';
import { fmtQty } from '../utils/numberFormat';
import { TabIcon, TAB_ICONS } from '../utils/tabIcons';
import { Button, Modal, FormField, EmptyState, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardStatusBadge, CardTabs, CardSplitButton } from '../components/card';
import CustomFieldsSection from '../components/CustomFieldsSection';

const W_TYPES = [
  { value: 'main', label: 'Ana Depo' },
  { value: 'branch', label: 'Şube Depo' },
  { value: 'retail_store', label: 'Perakende Mağaza' },
  { value: 'dealer', label: 'Bayi Depo' },
  { value: 'production', label: 'Üretim Depo' },
  { value: 'return', label: 'İade Depo' },
  { value: 'transit', label: 'Transit Depo' },
  { value: 'vehicle', label: 'Araç Depo' },
  { value: 'virtual', label: 'Sanal Depo' },
];

const emptyWarehouse = {
  code:'', name:'', warehouse_type:'main', address:'', city:'', district:'',
  phone:'', email:'', responsible_id:'', capacity:'', capacity_unit:'',
  notes:'', is_active:true, branch_id:''
};

function WarehousesPage() {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({...emptyWarehouse});
  const [isEditing, setIsEditing] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [personnel, setPersonnel] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ type: '', is_active: '' });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/warehouses', { params });
      if (res.data.success) { setList(res.data.data.items); setTotal(res.data.data.total); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, filters]);

  const fetchPersonnel = async () => {
    try {
      const res = await api.get('/personnel', { params: { limit: 100 } });
      if (res.data.success) setPersonnel(res.data.data.items);
    } catch (err) { console.error(err); }
  };

  const fetchBranches = async () => {
    try {
      const res = await api.get('/branches', { params: { is_active: 'yes' } });
      if (res.data.success) setBranches(res.data.data.items || []);
    } catch (err) { /* permission yoksa sessiz geç */ }
  };

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchPersonnel(); fetchBranches(); }, []);

  const handleSelect = async (w) => {
    try {
      const res = await api.get('/warehouses/' + w.id);
      if (res.data.success) {
        setSelected(res.data.data); setForm(res.data.data);
        setIsEditing(false); setIsNew(false); setActiveTab('general'); setMsg(null);
        setShowModal(true);
        const inv = await api.get('/warehouses/' + w.id + '/inventory');
        if (inv.data.success) setInventory(inv.data.data);
      }
    } catch (err) { console.error(err); }
  };

  const handleNew = () => {
    setSelected(null); setForm({...emptyWarehouse}); setIsEditing(true); setIsNew(true);
    setActiveTab('general'); setMsg(null); setInventory([]); setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false); setSelected(null); setForm({...emptyWarehouse});
    setIsEditing(false); setIsNew(false); setMsg(null);
  };

  const handleEdit = () => { setIsEditing(true); setMsg(null); };
  const handleCancel = () => {
    if (isNew) { handleModalClose(); }
    else { setForm(selected); setIsEditing(false); setMsg(null); }
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.code || !form.name) { setMsg({ type:'error', text:'Depo kodu ve adi zorunludur' }); return; }
    setSaving(true); setMsg(null);
    try {
      if (isNew) {
        const res = await api.post('/warehouses', form);
        if (res.data.success) {
          setMsg({ type:'success', text:'Depo olusturuldu' }); setIsNew(false); setIsEditing(false);
          fetchList(); handleSelect({ id: res.data.data.id });
        }
      } else {
        const res = await api.put('/warehouses/' + selected.id, form);
        if (res.data.success) { setMsg({ type:'success', text:'Depo guncellendi' }); setIsEditing(false); fetchList(); }
      }
      if (mode === 'close') setTimeout(() => handleModalClose(), 250);
      else if (mode === 'new') setTimeout(() => { setSelected(null); setForm({ ...emptyWarehouse }); setIsEditing(true); setIsNew(true); setMsg(null); }, 250);
    } catch (err) { setMsg({ type:'error', text: err.response?.data?.message || 'Hata' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    // Önce hareket var mı backend'e sor — varsa "emin misiniz" sormadan engelle
    try {
      const chk = await api.get('/warehouses/' + selected.id + '/movement-count');
      const cnt = chk.data?.data?.count ?? 0;
      if (cnt > 0) {
        window.alert(`⛔ Bu depoda ${cnt} hareket/fatura kaydı var.\n\nÖnce bağlı belgeleri silmeden depo silinemez.`);
        return;
      }
    } catch { /* endpoint yoksa eski akışa düş */ }
    if (!window.confirm(`"${selected.name || ''}" deposunu silmek istediğinize emin misiniz?`)) return;
    try {
      await api.delete('/warehouses/' + selected.id);
      handleModalClose(); fetchList();
    } catch(e) {
      if (e.response?.status === 409 && e.response.data?.hasMovements) {
        const cnt = e.response.data.movementCount || '';
        const txt = e.response.data.message || `Bu depoya ait ${cnt} hareket var — önce onları silin.`;
        window.alert('⛔ ' + txt);
        setMsg({ type: 'error', text: txt });
      } else {
        setMsg({ type: 'error', text: e.response?.data?.message || 'Silme hatası' });
      }
    }
  };

  const f = (key, val) => setForm(prev => ({...prev, [key]: val}));
  const tabs = [
    { key:'general',   label:'Genel',             icon:'general' },
    { key:'address',   label:'Adres & İletişim',  icon:'pin' },
    { key:'inventory', label:'Stok Durumu',       icon:'package' },
    { key:'custom',    label:'Özel',               icon:'sparkles' },
    { key:'notes',     label:'Notlar',            icon:'notes' },
  ];
  const typeLabel = (t) => W_TYPES.find(w => w.value === t)?.label || t;

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEdit = isEditing;
  const inView = !isEditing && !isNew;

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Depolar"
        count={total}
        unit="depo"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Kod veya ad ara..."
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={handleNew}>+ Yeni Depo</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'type', type: 'select', label: 'Tip',
            options: [{ value: '', label: 'Tümü' }, ...W_TYPES.map(t => ({ value: t.value, label: t.label }))] },
          { key: 'is_active', type: 'select', label: 'Durum',
            options: [{ value: '', label: 'Tümü' }, { value: 'yes', label: 'Aktif' }, { value: 'no', label: 'Pasif' }] },
        ]}
      />

      {/* TABLO */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey="warehouses-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Depo bulunamadı"
          data={list}
          rowKey="id"
          onRowClick={w => handleSelect(w)}
          columns={[
            { id: 'code', header: 'Depo Kodu', accessor: 'code', sortable: true,
              cell: w => <span className="stock-code">{w.code}</span>, width: 130 },
            { id: 'name', header: 'Depo Adi', accessor: 'name', sortable: true,
              cell: w => <span className="stock-name-cell">{w.name}</span> },
            { id: 'type', header: 'Tip', accessor: 'warehouse_type', sortable: true,
              cell: w => <span className="stock-badge active" style={{background:'rgba(59,130,246,0.1)',color:'var(--info)'}}>{typeLabel(w.warehouse_type)}</span>,
              width: 120 },
            { id: 'city', header: 'Sehir', accessor: 'city', sortable: true,
              cell: w => <span className="stock-secondary">{w.city || '-'}</span>, width: 120 },
            { id: 'responsible', header: 'Sorumlu', accessor: 'responsible_name', sortable: true,
              cell: w => <span className="stock-secondary">{w.responsible_name || '-'}</span>, width: 150 },
            { id: 'status', header: 'Durum', accessor: w => w.is_active ? 'Aktif' : 'Pasif', sortable: true,
              cell: w => <span className={'stock-badge ' + (w.is_active ? 'active' : 'passive')}>{w.is_active ? 'Aktif' : 'Pasif'}</span>,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' }, width: 90 },
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, width: 32, filterable: false,
              cellStyle: { textAlign: 'center' },
              cell: w => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/warehouses/${w.id}/audit`}/></span> },
          ]}
        />
      </div>

      {/* DETAY MODAL */}
      {showModal && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Depo' : 'Depo Kartı'}
          size="lg"
          onClose={handleModalClose}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && <Button variant="ghost" onClick={handleCancel}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={handleEdit}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {!isNew && (
            <CardHero
              icon="🏬"
              code={form.code}
              title={form.name}
              subtitle={[
                W_TYPES.find(t => t.value === form.warehouse_type)?.label,
                form.city,
                form.district,
              ].filter(Boolean).join(' · ')}
              statusBadge={<CardStatusBadge preset={form.is_active === false ? 'inactive' : 'active'} />}
              kpis={[
                form.capacity > 0 && { label: 'Kapasite', value: form.capacity, color: 'neutral', sublabel: form.capacity_unit || '' },
                form.phone && { label: 'Telefon', value: form.phone, color: 'info' },
                form.responsible_name && { label: 'Sorumlu', value: form.responsible_name.substring(0, 20), color: 'neutral' },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <CardTabs
            items={tabs.map(t => ({ key: t.key, label: t.label, icon: <TabIcon name={t.icon}/> }))}
            active={activeTab}
            onChange={setActiveTab}
          />

          {activeTab === 'general' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <FormField label="Depo Kodu" required>
                <input value={form.code||''} onChange={e=>f('code',e.target.value)} disabled={!isEditing}/>
              </FormField>
              <FormField label="Depo Adi" required>
                <input value={form.name||''} onChange={e=>f('name',e.target.value)} disabled={!isEditing}/>
              </FormField>
              <FormField label="Depo Tipi">
                <select value={form.warehouse_type||'main'} onChange={e=>f('warehouse_type',e.target.value)} disabled={!isEditing}>
                  {W_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </FormField>
              <FormField label="Bağlı Şube">
                <select value={form.branch_id||''} onChange={e=>f('branch_id',e.target.value||null)} disabled={!isEditing}>
                  <option value="">— Şube seçiniz —</option>
                  {branches.map(b=><option key={b.id} value={b.id}>{b.code ? b.code+' - ' : ''}{b.name}</option>)}
                </select>
              </FormField>
              <FormField label="Sorumlu Personel">
                <select value={form.responsible_id||''} onChange={e=>f('responsible_id',e.target.value||null)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  {personnel.map(p=><option key={p.id} value={p.id}>{p.full_name}</option>)}
                </select>
              </FormField>
              <FormField label="Kapasite">
                <input type="number" step="0.01" value={form.capacity||''} onChange={e=>f('capacity',parseFloat(e.target.value)||null)} disabled={!isEditing}/>
              </FormField>
              <FormField label="Kapasite Birimi">
                <select value={form.capacity_unit||''} onChange={e=>f('capacity_unit',e.target.value)} disabled={!isEditing}>
                  <option value="">Seciniz</option>
                  <option value="m2">m2</option>
                  <option value="m3">m3</option>
                  <option value="palet">Palet</option>
                  <option value="raf">Raf</option>
                </select>
              </FormField>
              <FormField label="Durum">
                <select value={form.is_active?'1':'0'} onChange={e=>f('is_active',e.target.value==='1')} disabled={!isEditing}>
                  <option value="1">Aktif</option>
                  <option value="0">Pasif</option>
                </select>
              </FormField>
            </div>
          )}

          {activeTab === 'address' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <FormField label="Telefon">
                <input value={form.phone||''} onChange={e=>f('phone',e.target.value)} disabled={!isEditing}/>
              </FormField>
              <FormField label="E-posta">
                <input value={form.email||''} onChange={e=>f('email',e.target.value)} disabled={!isEditing}/>
              </FormField>
              <FormField label="Sehir">
                <input value={form.city||''} onChange={e=>f('city',e.target.value)} disabled={!isEditing}/>
              </FormField>
              <FormField label="Ilce">
                <input value={form.district||''} onChange={e=>f('district',e.target.value)} disabled={!isEditing}/>
              </FormField>
              <FormField label="Adres" fullWidth>
                <textarea rows="3" value={form.address||''} onChange={e=>f('address',e.target.value)} disabled={!isEditing}/>
              </FormField>
            </div>
          )}

          {activeTab === 'inventory' && (
            <div>
              {inventory.length === 0 ? <EmptyState title="Stok yok" description="Bu depoda henuz stok yok" compact /> :
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
                <thead><tr style={{borderBottom:'1px solid var(--border-secondary)'}}>
                  <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Stok Kodu</th>
                  <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Stok Adi</th>
                  <th style={{textAlign:'right',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Miktar</th>
                  <th style={{textAlign:'right',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Rezerve</th>
                  <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Birim</th>
                  <th style={{textAlign:'left',padding:'8px',color:'var(--text-tertiary)',fontWeight:500}}>Raf</th>
                </tr></thead>
                <tbody>{inventory.map(i=>(
                  <tr key={i.id} style={{borderBottom:'1px solid var(--border-secondary)'}}>
                    <td style={{padding:'8px',fontFamily:'monospace',fontSize:'0.8rem',color:'var(--accent-primary)'}}>{i.stock_code}</td>
                    <td style={{padding:'8px'}}>{i.stock_name}</td>
                    <td style={{padding:'8px',textAlign:'right',fontWeight:600}}>{fmtQty(i.quantity)}</td>
                    <td style={{padding:'8px',textAlign:'right',color:'var(--warning)'}}>{i.reserved_qty > 0 ? fmtQty(i.reserved_qty) : '-'}</td>
                    <td style={{padding:'8px',color:'var(--text-secondary)'}}>{i.unit_code||'-'}</td>
                    <td style={{padding:'8px',color:'var(--text-secondary)'}}>{i.shelf_code||'-'}</td>
                  </tr>
                ))}</tbody>
              </table>}
            </div>
          )}

          {activeTab === 'custom' && selected?.id && (
            <CustomFieldsSection entityType="warehouse" entityId={selected.id} autoSave disabled={!isEditing} />
          )}
          {activeTab === 'custom' && !selected?.id && (
            <div style={{padding:20,color:'var(--text-tertiary)',fontSize:'0.9rem'}}>Özel alanlar için önce depoyu kaydedin.</div>
          )}

          {activeTab === 'notes' && (
            <FormField label="Notlar" fullWidth>
              <textarea rows="6" value={form.notes||''} onChange={e=>f('notes',e.target.value)} disabled={!isEditing}/>
            </FormField>
          )}
        </Modal>
      )}
    </div>
  );
}

export default WarehousesPage;
